import * as THREE from 'three';

/**
 * Manages audio loading, playback, and analysis using the Web Audio API
 * integrated with Three.js (THREE.AudioListener, THREE.Audio, THREE.AudioAnalyser).
 * Also handles the file input element for loading audio.
 */
export default class AudioManager {
    /**
     * Creates an AudioManager instance.
     * @param {THREE.Camera} camera - The main scene camera to attach the AudioListener to.
     */
    constructor(camera) {
        if (!camera) {
            throw new Error('AudioManager requires a THREE.Camera instance.');
        }
        /** @type {THREE.AudioListener} Listens to audio events, needs to be attached to the camera */
        this.listener = new THREE.AudioListener();
        camera.add(this.listener); // Attach listener to the camera provided by SceneManager

        /** @type {THREE.Audio} Represents the audio source being played */
        this.sound = new THREE.Audio(this.listener);
        /** @type {THREE.AudioAnalyser | null} Analyzes the audio frequency data */
        this.audioAnalyser = null;
        /** @type {AudioContext | null} The underlying Web Audio API context */
        this.audioContext = null; 
        /** @type {MediaStreamAudioSourceNode | null} Source node for microphone input */
        this.microphoneSource = null;
        /** @type {AnalyserNode | null} The underlying Web Audio API AnalyserNode */
        this.nativeAnalyser = null;
        /** @type {number} The AudioContext time when the current file playback started */
        this._playbackStartTime = 0; 
        /** @type {number} The offset within the audio buffer where playback started */
        this._playbackStartOffset = 0; 
        /** @type {number} Stores the playback time when paused */
        this._pausedTime = 0; 

        // Create and manage the file input element
        // REMOVED: this._createFileInput(); // Input creation moved to main.js/GuiManager
    }

    /**
     * Initializes the AudioContext if it doesn't exist or is closed.
     * Resumes the context if it's suspended.
     * @returns {Promise<AudioContext>} A promise that resolves with the active AudioContext.
     * @private
     */
    async _ensureAudioContext() {
        if (!this.audioContext || this.audioContext.state === 'closed') {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.listener.context = this.audioContext; // Link listener to this context
            console.log("AudioContext created.");
        } else if (this.audioContext.state === 'suspended') {
            console.log("AudioContext suspended. Resuming...");
            await this.audioContext.resume();
            console.log("AudioContext resumed.");
        }
        return this.audioContext;
    }

    /**
     * Loads the selected audio file, decodes it, and starts playback.
     * This method is now intended to be called externally with a File object.
     * @param {File} file - The audio file to load.
     */
    loadAndPlayFile(file) { // Renamed from _handleFileUpload and takes file directly
        // const file = event.target.files[0]; // REMOVED: File comes from parameter now
        if (!file) return; // Exit if no file was provided

        // Basic validation
        if (!file.type.startsWith('audio/')) {
            console.warn('Invalid file type. Please upload an audio file.');
            alert('请上传音频文件！'); // User feedback in Chinese
            // event.target.value = null; // REMOVED: Input is managed externally
            return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            const audioData = e.target.result; // Audio data as ArrayBuffer

            // --- Cleanup existing audio --- 
            if (this.sound.isPlaying) {
                this.sound.stop(); // Stop previous audio if playing
            }
             if (this.audioAnalyser) {
                 if (this.audioAnalyser.analyser) {
                    try {
                        // Disconnect the analyser node to prevent memory leaks
                        this.audioAnalyser.analyser.disconnect();
                    } catch (error) {
                        console.warn("Error disconnecting previous analyser:", error);
                    }
                 }
                 this.audioAnalyser = null; // Clear reference
             }

            // --- Setup Audio Context --- 
            // Reuse or create the AudioContext. Crucial for browser compatibility and resource management.
            this._setupAndPlay(audioData);
        };

        reader.onerror = (error) => {
            console.error('Error reading file:', error);
            alert('文件读取失败！'); // User feedback
            // event.target.value = null; // REMOVED: Input is managed externally
        };

        // Read the file contents as an ArrayBuffer
        reader.readAsArrayBuffer(file);
        // event.target.value = null; // REMOVED: Input is managed externally
    }
    
    /**
     * Sets up the THREE.Audio object with the decoded buffer, configures
     * the AudioAnalyser, and starts playback.
     * @param {ArrayBuffer} audioData - The audio data to decode and play.
     * @private
     */
    async _setupAndPlay(audioData) {
        const context = await this._ensureAudioContext(); // Ensure context is active and get it

        // --- Stop microphone if active ---
        this._disconnectMicrophone(); 

        try {
            // Decode the ArrayBuffer into an AudioBuffer
            const audioBuffer = await context.decodeAudioData(audioData);

            // --- Stop previous sound before setting new buffer ---
            if (this.sound.isPlaying) {
                this.sound.stop();
                this._resetPlaybackTimeTracking(); // Reset time tracking
            }
             // --- Cleanup analyser before setting new buffer ---
             if (this.audioAnalyser) {
                 if (this.audioAnalyser.analyser) {
                    try {
                        // Disconnect the analyser node to prevent memory leaks or issues
                        this.audioAnalyser.analyser.disconnect();
                    } catch (error) {
                        console.warn("Error disconnecting previous analyser before decode:", error);
                    }
                 }
                 this.audioAnalyser = null; // Clear reference
             }


            this.sound.setBuffer(audioBuffer); // Assign the decoded AudioBuffer
            this.sound.setLoop(false); // Loop the audio -> Disabled
            this.sound.setVolume(0.5); // Set a default volume (was incorrect reference)
            

            // Create a new THREE analyser for the currently playing sound
            const fftSize = 64; 
            // Use THREE.AudioAnalyser for file playback
            this.audioAnalyser = new THREE.AudioAnalyser(this.sound, fftSize); 
            // Clear native analyser reference if it exists from mic input
            this.nativeAnalyser = null; 
            console.log(`File audio decoded and playing. Analyser FFT size: ${fftSize}.`);
            
            // Start playback *after* setting up the buffer and analyser
            // Ensure previous playback is stopped and time tracking reset before playing new buffer
            if (this.sound.isPlaying) {
                this.sound.stop(); 
            }
            this._resetPlaybackTimeTracking(); // Reset time before playing
            
            this.sound.play(); 
            // Record the start time and offset when playback begins
            this._playbackStartOffset = this.sound.offset; // Get the starting offset
            this._playbackStartTime = this.audioContext.currentTime - this.sound.offset; // Use consistent audioContext
            this._pausedTime = 0; // Reset paused time
            console.log(`🎵 Playback started. Context Time: ${this.audioContext.currentTime.toFixed(3)}, Start Offset: ${this._playbackStartOffset.toFixed(3)}, Effective Start Time: ${this._playbackStartTime.toFixed(3)}`);

        } catch (error) {
            console.error('Error decoding audio data:', error);
            alert('无法解码音频文件！请尝试其他文件或格式。'); // User feedback
            this._disconnectMicrophone();
            // Also reset playback time tracking when disconnecting mic
            this._resetPlaybackTimeTracking();
        }
    }

    /**
     * Requests microphone access, creates an audio source, and connects it for analysis.
     */
    async startMicrophoneInput() {
        try {
            await this._ensureAudioContext(); // Ensure context is active

            // --- Stop file playback if active ---
            if (this.sound && this.sound.isPlaying) {
                this.sound.stop();
                console.log("Stopped file playback for microphone input.");
            }
            // Disconnect THREE's analyser if it exists
            if (this.audioAnalyser) {
                try {
                   if (this.audioAnalyser.analyser) {
                       this.audioAnalyser.analyser.disconnect();
                   }
                } catch(e) { console.warn("Error disconnecting THREE analyser:", e);}
                this.audioAnalyser = null; 
            }
            // --- Cleanup previous microphone source ---
            this._disconnectMicrophone();


            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            console.log("Microphone access granted.");

            this.microphoneSource = this.audioContext.createMediaStreamSource(stream);
            
            // Use the native Web Audio API AnalyserNode directly for mic input
            const fftSize = 64;
            this.nativeAnalyser = this.audioContext.createAnalyser();
            this.nativeAnalyser.fftSize = fftSize;

            // Connect microphone source to the native analyser
            this.microphoneSource.connect(this.nativeAnalyser);
            
            // DO NOT connect the microphone source to the listener's destination 
            // unless you want to hear the microphone input through the speakers.
            // this.microphoneSource.connect(this.listener.context.destination); 

            console.log(`Microphone input started. Native Analyser FFT size: ${fftSize}.`);

        } catch (error) {
            console.error('Error accessing microphone:', error);
            alert('无法访问麦克风，请检查权限。'); // User feedback
            // Handle error (e.g., show a message to the user)
        }
    }

    /**
     * Disconnects the microphone source and analyser node if they exist.
     * @private
     */
    _disconnectMicrophone() {
        if (this.microphoneSource) {
            // Disconnect from all destinations
            this.microphoneSource.disconnect(); 
            // Stop the tracks to release the microphone
            this.microphoneSource.mediaStream.getTracks().forEach(track => track.stop());
            this.microphoneSource = null;
            console.log("Microphone source disconnected and tracks stopped.");
            // Reset playback time tracking when microphone is disconnected
            this._resetPlaybackTimeTracking(); 
        }
         if (this.nativeAnalyser) {
             this.nativeAnalyser.disconnect(); // Disconnect analyser as well
             this.nativeAnalyser = null;
             console.log("Native analyser disconnected.");
         }
    }

    /**
     * Gets the average frequency value from the active AudioAnalyser (THREE or native).
     * @returns {number} The average frequency value (typically 0-255), or 0 if unavailable.
     */
    getAverageFrequency() {
        // Prioritize native analyser if mic is active
        if (this.nativeAnalyser) {
            // Need a buffer to store the frequency data
            const bufferLength = this.nativeAnalyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            this.nativeAnalyser.getByteFrequencyData(dataArray); // Fill the array

            // Calculate the average
            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
                sum += dataArray[i];
            }
            return sum / bufferLength;
        } 
        // Fallback to THREE's analyser if file is playing
        else if (this.audioAnalyser) {
            return this.audioAnalyser.getAverageFrequency();
        }
        return 0; // Return 0 if no analyser is active
    }

    /**
     * Loads audio from a URL, decodes it, and starts playback.
     * @param {string} url - The URL of the audio file to load.
     */
    async loadAndPlayUrl(url) {
        console.log(`Attempting to load audio from URL: ${url}`);
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const audioData = await response.arrayBuffer(); // Get data as ArrayBuffer

            console.log(`Audio data fetched successfully from ${url}. Size: ${audioData.byteLength} bytes.`);

            // --- Cleanup existing audio (similar to loadAndPlayFile) ---
            if (this.sound.isPlaying) {
                this.sound.stop();
                console.log("Stopped previous sound for URL playback.");
            }
            if (this.audioAnalyser) {
                if (this.audioAnalyser.analyser) {
                   try {
                       this.audioAnalyser.analyser.disconnect();
                   } catch (error) {
                       console.warn("Error disconnecting previous analyser before URL load:", error);
                   }
                }
                this.audioAnalyser = null; 
            }
             // --- Stop microphone if active ---
             this._disconnectMicrophone(); 

            // --- Setup and Play ---
            // Use the existing private method to handle decoding and playback
             console.log("Calling _setupAndPlay for URL audio...");
            await this._setupAndPlay(audioData); 
            console.log(`Successfully started playback for ${url}.`);

        } catch (error) {
            console.error(`Error loading or playing audio from URL ${url}:`, error);
            // Optionally show a user-friendly error message
             alert(`无法从URL加载默认音频：${error.message}`);
        }
    }

    /**
     * Resets the internal playback time tracking variables.
     * @private
     */
     _resetPlaybackTimeTracking() {
        this._playbackStartTime = 0;
        this._playbackStartOffset = 0;
        this._pausedTime = 0;
        this._fallbackStartTime = null; // Reset fallback timing
        
        // 🔄 Additional reset for production environment
        if (this.sound) {
            this.sound.offset = 0; // Reset audio offset
        }
        
        console.log("⏱️ Playback time tracking completely reset.");
    }

    /**
     * Gets the underlying AudioContext.
     * Note: The context might be null if no audio has been initialized.
     * Use _ensureAudioContext internally if you need to guarantee it exists.
     * @returns {AudioContext | null} The Web Audio API AudioContext.
     */
    getAudioContext() {
        return this.audioContext;
    }

    /**
     * Checks if the main audio source (this.sound) is currently playing.
     * Does not reflect microphone input state.
     * @returns {boolean} True if audio is playing, false otherwise.
     */
    isPlaying() {
        return this.sound && this.sound.isPlaying;
    }

    /**
     * Calculates the approximate current playback time of the audio file in seconds.
     * This is based on the AudioContext's clock and needs careful handling of pauses/stops.
     * TODO: Improve accuracy, especially with pause/resume and seeking.
     * @returns {number} The estimated current playback time in seconds, or 0 if not playing.
     */
    getCurrentPlaybackTime() {
        // Enhanced validation for production environment
        if (!this.audioContext) {
            console.warn('⚠️ AudioContext not available');
            return 0;
        }
        
        if (!this.isPlaying()) {
             // If paused, return the time at which it was paused
             if (this._pausedTime > 0) {
                 return this._pausedTime;
             }
             return 0; 
        }
        
        if (this._playbackStartTime === 0) {
            console.warn('⚠️ Playback start time not set');
            return 0;
        }
        
        // Use a fallback if audioContext seems problematic
        let contextTime;
        try {
            contextTime = this.audioContext.currentTime;
            // Additional check for reasonable context time
            if (contextTime < 0 || contextTime > 1000000) {
                throw new Error('AudioContext currentTime seems invalid');
            }
        } catch (error) {
            console.warn('⚠️ AudioContext currentTime error:', error);
            // Fallback to manual timing (less accurate)
            if (!this._fallbackStartTime) {
                this._fallbackStartTime = Date.now() / 1000;
            }
            contextTime = (Date.now() / 1000) - this._fallbackStartTime + this._playbackStartOffset;
        }
        
        // Current time based on context clock minus the adjusted start time
        const elapsed = contextTime - this._playbackStartTime;
        
        // 🐛 DEBUG: Add logging for production debugging
        if (elapsed < 0 || elapsed > 10000) { // Suspicious values
            console.warn(`⚠️ Suspicious playback time detected:`, {
                elapsed: elapsed.toFixed(3),
                contextTime: contextTime.toFixed(3),
                startTime: this._playbackStartTime.toFixed(3),
                offset: this._playbackStartOffset.toFixed(3),
                isPlaying: this.isPlaying(),
                audioContextState: this.audioContext.state
            });
        }
        
        // Ensure we return a positive, reasonable value
        const clampedElapsed = Math.max(0, elapsed);
        const result = clampedElapsed + this._playbackStartOffset;
        
        // Additional sanity check
        const duration = this.getDuration();
        if (duration > 0 && result > duration + 1) { // Allow 1 second buffer
            console.warn(`⚠️ Playback time exceeds duration: ${result.toFixed(3)} > ${duration.toFixed(3)}`);
            return duration;
        }
        
        return result;
    }

    /**
     * Pauses the audio playback and records the time.
     * TODO: THREE.Audio's pause might handle this internally, need verification.
     */
     pause() {
        // Calculate time *before* pausing, as isPlaying() changes immediately
        let currentTimeBeforePause = 0;
        if (this.isPlaying() && this.audioContext && this._playbackStartTime !== 0) {
             currentTimeBeforePause = this.audioContext.currentTime - this._playbackStartTime;
        }

        if (this.isPlaying()) { // Check again, in case of race conditions (unlikely but safe)
            this.sound.pause(); // Use THREE.Audio's pause
            // Record the calculated playback time when pausing
            this._pausedTime = currentTimeBeforePause;
            console.log(`Playback paused at calculated time: ${this._pausedTime.toFixed(3)}`);
        } else {
             console.log("Pause called but not playing.");
        }
    }

    /**
     * Resumes audio playback.
     * TODO: Accurate resume needs to consider the time spent paused.
     * THREE.Audio's play might handle offset correctly, need verification.
     */
     resume() {
        if (!this.isPlaying() && this.sound.buffer && this._pausedTime > 0) {
             // THREE.Audio's play() typically resumes from where it paused if called after pause()
             // However, our manual time tracking needs adjustment.
             console.log(`Attempting resume from: ${this._pausedTime.toFixed(3)}`);
             
             // We need to adjust _playbackStartTime based on the duration of the pause.
             // The new effective start time is the current context time minus the time where it was paused.
             const pauseDuration = this.audioContext.currentTime - (this._playbackStartTime + this._pausedTime);
             this._playbackStartTime += pauseDuration; // Adjust start time forward by the pause duration

             this.sound.play(); // Resume playback
             const resumeOffset = this.sound.offset; // Check the offset THREE.Audio is using
             console.log(`Playback resumed. Context Time: ${this.audioContext.currentTime.toFixed(3)}, Resume Offset: ${resumeOffset.toFixed(3)}, New Effective Start Time: ${this._playbackStartTime.toFixed(3)}`);
             this._pausedTime = 0; // Clear paused time marker
        } else if (!this.isPlaying() && this.sound.buffer) {
            // If trying to resume when not paused (e.g., after stop or initial load), just play
            console.log("Resume called, but not paused. Playing from offset:", this.sound.offset);
            this.sound.play();
             // Reset time tracking as if starting fresh
            this._playbackStartOffset = this.sound.offset; 
            this._playbackStartTime = this.audioContext.currentTime - this.sound.offset;
            this._pausedTime = 0;
        }
    }

    /**
     * Sets the playback volume.
     * @param {number} value - Volume level (0.0 to 1.0).
     */
    setVolume(value) {
        if (this.sound) {
            // Clamp value between 0 and 1
            const clampedValue = Math.max(0, Math.min(1, value));
            this.sound.setVolume(clampedValue);
            console.log(`Volume set to: ${clampedValue}`);
        } else {
            console.warn("Cannot set volume: Sound object not initialized.");
        }
    }

    /**
     * Gets the duration of the currently loaded audio buffer.
     * @returns {number} Duration in seconds, or 0 if no buffer is loaded.
     */
    getDuration() {
        if (this.sound && this.sound.buffer) {
            return this.sound.buffer.duration;
        }
        return 0;
    }

    /**
     * Seeks to a specific time in the audio playback.
     * Stops the current playback and restarts it from the specified time.
     * If seeking to time 0 (or very close), it *always* starts playback.
     * @param {number} time - The time to seek to, in seconds.
     */
    seek(time) {
        if (!this.sound || !this.sound.buffer) {
            console.warn("Cannot seek: No audio buffer loaded.");
            return;
        }

        const duration = this.getDuration();
        // Clamp seek time within valid range [0, duration]
        const seekTime = Math.max(0, Math.min(time, duration));
        const isSeekingToStart = seekTime < 0.1; // Check if seeking near the beginning

        console.log(`Seeking to: ${seekTime.toFixed(3)}s`);

        const wasPlaying = this.isPlaying();

        // Stop current playback to apply offset correctly
        if (this.isPlaying()) {
            this.sound.stop();
             // Reset playback tracking as stop clears internal state
             this._resetPlaybackTimeTracking();
        } else {
            // If paused, clear the paused state as we are setting a new time
             this._pausedTime = 0;
        }

        // Set the offset for the next playback
        this.sound.offset = seekTime;
        this._playbackStartOffset = seekTime; // Update our tracking offset

        // Restart playback if it was playing before seeking OR if seeking to the start
        if (wasPlaying || isSeekingToStart) {
            this.sound.play(); // Start playback from the new offset
            // Update start time based on the new offset
            this._playbackStartTime = this.audioContext.currentTime - seekTime;
            this._pausedTime = 0; // Ensure paused state is cleared
            if (isSeekingToStart) {
                 console.log(`🎯 Playback force-started after seek to beginning at context time: ${this.audioContext.currentTime.toFixed(3)}`);
            } else {
                console.log(`🎯 Playback restarted after seek at context time: ${this.audioContext.currentTime.toFixed(3)}, new effective start: ${this._playbackStartTime.toFixed(3)}`);
            }
        } else {
             // If it wasn't playing and not seeking to start, just update the offset.
             // The next call to resume() will use this offset.
             // Store the seek target as the 'paused at' time
             this._pausedTime = seekTime; 
             console.log(`Seek position set to ${seekTime.toFixed(3)}s while paused/stopped.`);
        }
    }

    // --- Public method to trigger file selection --- 
    // REMOVED - now handled by GuiManager triggering hidden input in main.js
} 